import { useEffect, useCallback } from 'react';
import {
  PostJSON,
  BaseResponse,
  ErrorResponse,
  PaginatedParams,
  IDParams,
  GetJSON
} from './request';
import useRequestJSON, {
  useRequestOnEvent,
  State,
  RunnerSegment
} from './hooks/useRequestJSON';
import { useAuth, transformUserResponse } from '../contexts/auth';
import { IAccount } from './account';

export interface UserConfigs {
  pinnedTemplateIds?: number[];
}

export interface IUser {
  id: number;
  accountId: number;
  account?: IAccount;
  email: string;
  phone: string;
  password: string;
  isAdmin: boolean;
  isPendingInvitation: boolean;
  hasConfirmedEmail: boolean;
  confirmedEmailAt: Date;
  confirmedEmailToken: string;
  resetToken: string;
  resetTokenExpiresAt: Date;
  passwordToken: string;
  lastLoginAt: Date;
  lastPassChangedAt: Date;
  failedLogins: number;
  firstFailedAt: Date;
  lockedAt: Date;
  defaultApp: string;
  defaultContactId: number;
  configs: UserConfigs;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
}

// Update User Configs

export interface UpdateUserConfigsParams {
  pinnedTemplateIds?: number[];
}

interface UpdateUserConfigsResponse extends BaseResponse {
  user: IUser;
}

export const UpdateUserConfigs = (
  params: UpdateUserConfigsParams
): Promise<UpdateUserConfigsResponse | ErrorResponse> => {
  return PostJSON<UpdateUserConfigsResponse>({
    url: '/user/update_configs/',
    params
  });
};

// Request Password Reset

export interface RequestResetParams {
  email: string;
}

interface RequestResetResponse {
  status: string;
}

interface UseRequestResetResponse<E> extends State<E> {
  reset: (email: string) => void;
}

/**
 * Used to kick off a reset password request
 */
export const useRequestResetPassword =
  (): UseRequestResetResponse<RequestResetResponse> => {
    const { data, isLoading, error, run } = useRequestOnEvent<
      RequestResetParams,
      RequestResetResponse
    >();

    const reset = (email: string) => {
      const req = () =>
        PostJSON<RequestResetResponse, RequestResetParams>({
          url: '/user/request_reset/',
          params: { email }
        });
      run(req);
    };

    return {
      data,
      isLoading,
      error,
      reset
    };
  };

// Login

interface LoginParams {
  email: string;
  password: string;
  transformFn?: (response: UserResponse) => IUser;
}

interface UserResponse {
  status: string;
  user: IUser;
}

interface UseLoginResponse<E> extends State<E> {
  login: (params: LoginParams) => void;
}

/**
 * Used to log in a user
 */
export const useLogin = (): UseLoginResponse<IUser> => {
  const { updateUser } = useAuth();
  const { data, isLoading, error, run } = useRequestOnEvent<
    LoginParams,
    IUser
  >();

  const userId = data ? data.id : null;

  /**
   * We use userId as the useEffect dependency to avoid
   * the useEffect getting called more than necessary
   *
   * Keying off the user id is a bit of a shortcut so we don't have to
   * pass the entire data user object as a dependency and then do something
   * fancy with useMemo to ensure the effect doesn't run more than we want
   */
  useEffect(() => {
    updateUser(data);
  }, [userId]);

  const login = ({ email, password }) => {
    const req = () =>
      PostJSON<UserResponse, LoginParams>({
        url: '/user/auth/',
        params: { email, password }
      });
    run(req, transformUserResponse);
  };

  return {
    data,
    isLoading,
    error,
    login
  };
};

interface CreateUserParams {
  email: string;
  phone: string;
  password: string;
  type?: string; // account type
}

interface UseCreateUserValue {
  data: BaseResponse;
  isLoading: boolean;
  error: any;
  resetRequest: () => void;
  createUser: (params: CreateUserParams) => void;
}

/**
 * Used to register a new user
 */
export const useCreateUser = (): UseCreateUserValue => {
  const { data, isLoading, error, resetRequest, run } = useRequestOnEvent<
    CreateUserParams,
    BaseResponse
  >();

  const createUser = (params: CreateUserParams) => {
    const req = () =>
      PostJSON<BaseResponse, CreateUserParams>({
        url: '/user/create/',
        params
      });
    run(req);
  };

  return {
    data,
    isLoading,
    error,
    resetRequest,
    createUser
  };
};

interface InvitedUserResponse extends BaseResponse {
  user: IUser;
}

/**
 * Used to check if an invited user has a valid token
 */
export const useGetInvitedUser = (
  token: string
): Omit<InvitedUserResponse, 'status'> &
  Pick<State<InvitedUserResponse>, 'isLoading' | 'error'> => {
  const request = useCallback(
    () =>
      GetJSON({
        url: '/user/invited/',
        params: {
          token
        }
      }),
    [token]
  );
  const { data, isLoading, error } = useRequestJSON<InvitedUserResponse>({
    request
  });
  return {
    user: data?.user,
    isLoading,
    error
  };
};

/**
 * Single Sign On
 */

interface SSOEligibilityResponse extends BaseResponse {
  hasSSO: boolean;
  isBreakGlass: boolean;
}

const REGEX_DOMAIN =
  /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9][a-z0-9-]{0,61}[a-z0-9]$/;

export const useHasSSO = (
  realm: string,
  email?: string
): Omit<SSOEligibilityResponse, 'status'> & RunnerSegment => {
  const request = useCallback(
    () =>
      PostJSON({
        url: '/user/sso_eligibility/',
        params: {
          realm,
          email
        }
      }),
    [realm, email]
  );
  const { data, run } = useRequestJSON<SSOEligibilityResponse>({
    request,
    enabled: realm != null && REGEX_DOMAIN.test(realm)
  });
  return {
    hasSSO: data?.hasSSO,
    isBreakGlass: data?.isBreakGlass,
    run: () => run(request)
  };
};

/**
 * Data hooks
 */

export interface GetUsersResponse extends BaseResponse {
  users: IUser[];
  total: number;
  isLoading?: boolean;
}

interface GetUsersFn {
  (params: PaginatedParams): Promise<GetUsersResponse | ErrorResponse>;
}

export const transformUsers = (
  response: GetUsersResponse
): Omit<GetUsersResponse, 'status'> => {
  return {
    users: response.users,
    total: response.total
  };
};

export const GetUsers: GetUsersFn = (params) =>
  PostJSON<GetUsersResponse, PaginatedParams>({
    url: '/user/data/',
    params
  });

export const useGetUsers = (
  params?: PaginatedParams & { searchTerm?: string }
): Omit<GetUsersResponse, 'status'> & RunnerSegment => {
  params = params || {
    page: 0,
    limit: 5
  };
  const request = useCallback(
    () => GetUsers(params),
    [params.page, params.limit, params.searchTerm]
  );
  const { data, run, isLoading } = useRequestJSON<
    GetUsersResponse,
    Omit<GetUsersResponse, 'status'>
  >({
    request,
    transformFn: transformUsers
  });
  return {
    users: data?.users,
    total: data?.total,
    run: () => run(request),
    isLoading
  };
};

export interface GetUsersByIDParams {
  ids: number[];
}

interface GetUsersByIDFn {
  (params: GetUsersByIDParams): Promise<GetUsersResponse | ErrorResponse>;
}

export const GetUsersByID: GetUsersByIDFn = (params) =>
  PostJSON<GetUsersResponse, GetUsersByIDParams>({
    url: '/user/data/',
    params
  });

export const useGetUsersByID = (
  params: GetUsersByIDParams
): Omit<GetUsersResponse, 'status'> & RunnerSegment => {
  const request = useCallback(() => GetUsersByID(params), [params.ids]);
  const { data, run } = useRequestJSON<
    GetUsersResponse,
    Omit<GetUsersResponse, 'status'>
  >({
    request,
    transformFn: transformUsers
  });
  return {
    users: data ? data.users : undefined,
    total: data ? data.total : undefined,
    run: () => run(request)
  };
};

// Request functions

interface LoginUserResponse extends BaseResponse {
  user?: IUser;
  phoneEnding?: string;
}

export const LoginUser = (params: LoginParams): Promise<LoginUserResponse> => {
  return PostJSON<LoginUserResponse, LoginParams>({
    url: '/user/auth/',
    params
  });
};

export const RemoveUser = (
  params: IDParams
): Promise<BaseResponse | ErrorResponse> => {
  return PostJSON<BaseResponse>({
    url: '/user/remove/',
    params
  });
};

interface ReinviteUserResponse extends BaseResponse {
  task: string;
}

export const ReinviteUser = (
  params: IDParams
): Promise<ReinviteUserResponse | ErrorResponse> => {
  return PostJSON<ReinviteUserResponse>({
    url: '/user/resend_invitation/',
    params
  });
};
